import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const GUEST_KEY = "p4p_guest_cart_v1";

function loadGuest() {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}
function saveGuest(items) {
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(items)); } catch {}
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load server cart when logged in
  const refresh = useCallback(async () => {
    if (!user) {
      const guest = loadGuest();
      const subtotal = guest.reduce((s, it) => s + (Number(it.unit_price || 0) * Number(it.quantity || 0)), 0);
      const count = guest.reduce((s, it) => s + Number(it.quantity || 0), 0);
      setCart({ items: guest, subtotal: Math.round(subtotal * 100) / 100, count });
      return;
    }
    try {
      const r = await api.get("/cart");
      setCart(r.data);
    } catch {
      setCart({ items: [], subtotal: 0, count: 0 });
    }
  }, [user]);

  // On login: merge guest cart -> server cart
  useEffect(() => {
    (async () => {
      if (user) {
        const guest = loadGuest();
        if (guest.length > 0) {
          try {
            await api.post("/cart/merge", { items: guest.map((g) => ({ peptide_slug: g.peptide_slug, variant_id: g.variant_id, quantity: g.quantity })) });
            localStorage.removeItem(GUEST_KEY);
          } catch {}
        }
      }
      await refresh();
    })();
  }, [user, refresh]);

  const addItem = useCallback(async ({ peptide_slug, variant_id, quantity = 1, name, unit_price, variant_label, image_url }) => {
    setLoading(true);
    try {
      if (!user) {
        const guest = loadGuest();
        const existing = guest.find((g) => g.peptide_slug === peptide_slug && g.variant_id === variant_id);
        if (existing) {
          existing.quantity = Number(existing.quantity || 0) + Number(quantity);
        } else {
          guest.push({
            line_id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            peptide_slug, variant_id, quantity, name, unit_price, variant_label, image_url,
            line_total: Number(unit_price || 0) * Number(quantity),
          });
        }
        saveGuest(guest);
        await refresh();
        toast.success(`Added to cart · ${name}${variant_label ? " · " + variant_label : ""}`);
        setOpen(true);
        return;
      }
      const r = await api.post("/cart/items", { peptide_slug, variant_id, quantity });
      setCart(r.data);
      toast.success(`Added to cart · ${name || peptide_slug}${variant_label ? " · " + variant_label : ""}`);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  }, [user, refresh]);

  const updateQty = useCallback(async (line_id, quantity) => {
    if (!user) {
      let guest = loadGuest();
      if (quantity <= 0) {
        guest = guest.filter((g) => g.line_id !== line_id);
      } else {
        guest = guest.map((g) => g.line_id === line_id ? { ...g, quantity, line_total: Number(g.unit_price || 0) * quantity } : g);
      }
      saveGuest(guest);
      await refresh();
      return;
    }
    const r = await api.patch(`/cart/items/${line_id}`, { quantity });
    setCart(r.data);
  }, [user, refresh]);

  const removeItem = useCallback(async (line_id) => {
    if (!user) {
      const guest = loadGuest().filter((g) => g.line_id !== line_id);
      saveGuest(guest);
      await refresh();
      return;
    }
    const r = await api.delete(`/cart/items/${line_id}`);
    setCart(r.data);
  }, [user, refresh]);

  const clear = useCallback(async () => {
    if (!user) { saveGuest([]); await refresh(); return; }
    const r = await api.delete("/cart");
    setCart(r.data);
  }, [user, refresh]);

  const value = useMemo(() => ({
    cart, loading, open, setOpen, addItem, updateQty, removeItem, clear, refresh,
  }), [cart, loading, open, addItem, updateQty, removeItem, clear, refresh]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
